import * as ToastPrimitive from "@radix-ui/react-toast";
import { createContext, useState, useMemo, useEffect, useContext } from "react";
import styled, { css, keyframes } from "styled-components";
import { nanoid } from "nanoid";
import Box from "../box";
import Button from "../button";
import { useCallbackRef } from "../../hooks";
import { omit } from "../../utils/object-util";
import { CloseOutlined, CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from "../../icons";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ToastContext = /*#__PURE__*/createContext(null);
export function useToast() {
  return useContext(ToastContext);
}
const hide = keyframes(["from{margin-bottom:0;opacity:1;}to{margin-bottom:-16px;opacity:0;}"]);
const slideIn = keyframes(["from{transform:translateX(calc(100% + var(--viewport-padding)));}to{transform:translateX(0);}"]);
const swipeOut = keyframes(["from{transform:translateX(var(--radix-toast-swipe-end-x));}to{transform:translateX(calc(100% + var(--viewport-padding)));}"]);
const makeTypeColor = (type, color) => p => p.type === type && css(["color:", ";"], p.theme.colors[color].base);
const ToastIcon = styled.span.withConfig({
  displayName: "toast__ToastIcon",
  componentId: "sc-1mlaxzp-0"
})(["font-size:", "px;", " ", " ", " ", ""], p => p.theme.fontSizes[5], makeTypeColor("info", "info"), makeTypeColor("success", "positive"), makeTypeColor("error", "negative"), makeTypeColor("warning", "warning"));
const ToastTitle = styled(ToastPrimitive.Title).withConfig({
  displayName: "toast__ToastTitle",
  componentId: "sc-1mlaxzp-1"
})(["color:", ";font-size:", "px;"], p => p.theme.colors.heading, p => p.theme.fontSizes.lg);
const ToastDescription = styled(ToastPrimitive.Description).withConfig({
  displayName: "toast__ToastDescription",
  componentId: "sc-1mlaxzp-2"
})(["font-size:", "px;"], p => p.theme.fontSizes.md);
const ToastAction = styled(ToastPrimitive.Action).withConfig({
  displayName: "toast__ToastAction",
  componentId: "sc-1mlaxzp-3"
})(["flex-shrink:0;"]);
const ToastClose = styled(ToastPrimitive.Close).withConfig({
  displayName: "toast__ToastClose",
  componentId: "sc-1mlaxzp-4"
})(["flex-shrink:0;color:", ";"], p => p.theme.colors.gray.base);
const ToastRoot = styled(ToastPrimitive.Root).withConfig({
  displayName: "toast__ToastRoot",
  componentId: "sc-1mlaxzp-5"
})(["display:flex;align-items:center;column-gap:", "px;padding:", "px;border-radius:", "px;box-shadow:", ";background:", ";line-height:", ";&:focus-visible{outline:4px solid ", ";}&[data-state=\"open\"]{animation:", " 0.15s ", ";}&[data-state=\"closed\"]{animation:", " 0.1s ", ";}&[data-swipe=\"move\"]{transform:translateX(var(--radix-toast-swipe-move-x));}&[data-swipe=\"cancel\"]{transform:translateX(0);transition:transform 0.2s ", ";}&[data-swipe=\"end\"]{animation:", " 0.1s ", ";}"], p => p.theme.space.md, p => p.theme.space.md, p => p.theme.radii.base, p => p.theme.shadows.base, p => p.theme.colors.popover.bg, p => p.theme.lineHeights.base, p => p.theme.colors.focus, slideIn, p => p.theme.transitions.easeOut, hide, p => p.theme.transitions.easeIn, p => p.theme.transitions.easeOut, swipeOut, p => p.theme.transitions.easeOut);
const ToastViewport = styled(ToastPrimitive.Viewport).withConfig({
  displayName: "toast__ToastViewport",
  componentId: "sc-1mlaxzp-6"
})(["--viewport-padding:16px;position:fixed;bottom:8px;right:0;display:flex;flex-direction:column;padding:var(--viewport-padding);gap:16px;width:420px;max-width:100vw;margin:0;list-style:none;z-index:1010;outline:none;"]);
const iconMap = {
  info: InfoCircleFilled,
  success: CheckCircleFilled,
  error: CloseCircleFilled,
  warning: ExclamationCircleFilled
};
export function Toast(_ref) {
  let {
    title,
    description,
    action,
    actionAltText = "Action",
    duration = 5000,
    type,
    sensitivity = "foreground",
    onClose
  } = _ref;
  const [open, setOpen] = useState(true);
  const iconType = type ? iconMap[type] : null;
  useEffect(() => {
    if (!open) {
      const id = setTimeout(() => onClose?.(), 200);
      return () => clearTimeout(id);
    }
    return;
  }, [open]);
  return /*#__PURE__*/_jsxs(ToastRoot, {
    open: open,
    onOpenChange: setOpen,
    type: sensitivity,
    duration: duration,
    children: [iconType ? /*#__PURE__*/_jsx(ToastIcon, {
      as: iconType,
      type: type
    }) : null, /*#__PURE__*/_jsxs(Box, {
      flexGrow: 1,
      children: [/*#__PURE__*/_jsx(ToastTitle, {
        children: title
      }), description ? /*#__PURE__*/_jsx(ToastDescription, {
        children: description
      }) : null]
    }), action ? /*#__PURE__*/_jsx(ToastAction, {
      asChild: true,
      altText: actionAltText,
      children: action
    }) : null, /*#__PURE__*/_jsx(ToastClose, {
      asChild: true,
      children: /*#__PURE__*/_jsx(Button, {
        type: "text",
        icon: /*#__PURE__*/_jsx(CloseOutlined, {})
      })
    })]
  });
}
export function ToastProvider(_ref2) {
  let {
    children
  } = _ref2;
  const [toasts, setToasts] = useState({});
  const open = useCallbackRef(props => {
    const key = props.key ?? nanoid();
    setToasts(t => {
      return {
        ...t,
        [key]: {
          ...t[key],
          ...props,
          key
        }
      };
    });
    return key;
  });
  const close = useCallbackRef(key => {
    if (toasts[key]) {
      setToasts(t => {
        return {
          ...t,
          [key]: {
            ...t[key],
            duration: 1
          }
        };
      });
    }
  });
  const info = useCallbackRef(props => {
    return open({
      ...props,
      type: "info"
    });
  });
  const success = useCallbackRef(props => {
    return open({
      ...props,
      type: "success"
    });
  });
  const error = useCallbackRef(props => {
    return open({
      ...props,
      type: "error"
    });
  });
  const warning = useCallbackRef(props => {
    return open({
      ...props,
      type: "warning"
    });
  });
  const contextValue = useMemo(() => {
    return {
      open,
      close,
      info,
      success,
      error,
      warning
    };
  }, []);
  const handleClose = useCallbackRef(key => {
    setToasts(t => omit(t, [key]));
    toasts[key]?.onClose?.();
  });
  return /*#__PURE__*/_jsx(ToastContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsxs(ToastPrimitive.Provider, {
      swipeDirection: "right",
      children: [children, Object.values(toasts).map(_ref3 => {
        let {
          key,
          ...props
        } = _ref3;
        return /*#__PURE__*/_jsx(Toast, {
          ...props,
          onClose: () => handleClose(key)
        }, key);
      }), /*#__PURE__*/_jsx(ToastViewport, {})]
    })
  });
}